import {
  CASHBOOK_VIEW_MODES,
  type CashbookViewMode,
  localTodayIso
} from '@finvox/shared/cashbook';
import { firstAllowedCashbookViewMode, type CashbookAccess } from '@finvox/shared/cashbook-access';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_RE = /^\d{4}-\d{2}$/;
const YEAR_RE = /^\d{4}$/;

function isValidCalendarDate(value: string): boolean {
  if (!DATE_RE.test(value)) return false;
  const [ys, ms, ds] = value.split('-');
  const y = Number(ys);
  const m = Number(ms);
  const d = Number(ds);
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

function isValidCalendarMonth(value: string): boolean {
  if (!MONTH_RE.test(value)) return false;
  return isValidCalendarDate(`${value}-01`);
}

export type CashbookPeriodUrlState = {
  viewMode: CashbookViewMode;
  selectedDate: string;
  selectedMonth: string;
  selectedYear: string;
  rangeStartMonth: string;
  rangeEndMonth: string;
};

function isViewMode(value: string | null): value is CashbookViewMode {
  return Boolean(value && (CASHBOOK_VIEW_MODES as readonly string[]).includes(value));
}

function allowedView(
  view: CashbookViewMode,
  access: CashbookAccess | null | undefined
): CashbookViewMode {
  if (!access) return view;
  if (access.viewModes[view]) return view;
  return firstAllowedCashbookViewMode(access);
}

function validDate(value: string | null, fallback: string): string {
  return value && isValidCalendarDate(value) ? value : fallback;
}

function validMonth(value: string | null, fallback: string): string {
  return value && isValidCalendarMonth(value) ? value : fallback;
}

function validYear(value: string | null, fallback: string): string {
  if (!value || !YEAR_RE.test(value)) return fallback;
  const y = Number(value);
  if (y < 1970 || y > 2100) return fallback;
  return value;
}

/** Read cashbook period state from URL search params. Invalid values fall back safely. */
export function parseCashbookPeriodSearchParams(
  params: URLSearchParams,
  access: CashbookAccess | null | undefined,
  defaults?: Partial<CashbookPeriodUrlState>
): CashbookPeriodUrlState {
  const today = localTodayIso();
  const fallback: CashbookPeriodUrlState = {
    viewMode: 'day',
    selectedDate: today,
    selectedMonth: today.slice(0, 7),
    selectedYear: today.slice(0, 4),
    rangeStartMonth: today.slice(0, 7),
    rangeEndMonth: today.slice(0, 7),
    ...defaults
  };

  const rawView = params.get('view');
  const viewMode = allowedView(isViewMode(rawView) ? rawView : fallback.viewMode, access);

  return {
    viewMode,
    selectedDate: validDate(params.get('date'), fallback.selectedDate),
    selectedMonth: validMonth(params.get('month'), fallback.selectedMonth),
    selectedYear: validYear(params.get('year'), fallback.selectedYear),
    rangeStartMonth: validMonth(params.get('from'), fallback.rangeStartMonth),
    rangeEndMonth: validMonth(params.get('to'), fallback.rangeEndMonth)
  };
}

/** Build query string for the current cashbook period (no leading ?). */
export function buildCashbookPeriodSearchParams(state: CashbookPeriodUrlState): string {
  const params = new URLSearchParams();
  params.set('view', state.viewMode);
  if (state.viewMode === 'day') {
    params.set('date', state.selectedDate);
  } else if (state.viewMode === 'month') {
    params.set('month', state.selectedMonth);
  } else if (state.viewMode === 'year') {
    params.set('year', state.selectedYear);
  } else {
    params.set('from', state.rangeStartMonth);
    params.set('to', state.rangeEndMonth);
  }
  return params.toString();
}

export type CashbookPeriodDrillTarget =
  | { kind: 'day'; date: string }
  | { kind: 'month'; month: string };

export function canDrillToPeriod(
  target: CashbookPeriodDrillTarget,
  access: CashbookAccess | null | undefined
): boolean {
  if (!access) return true;
  if (target.kind === 'day') return access.viewModes.day ?? true;
  return access.viewModes.month ?? true;
}
